import { toBlob } from 'html-to-image';

const MAX_EXPORT_PIXELS_MOBILE = 2000000;
const MAX_EXPORT_PIXELS_DESKTOP = 8000000;
const MAX_EXPORT_DIMENSION = 2048;
const MIN_BLOB_BYTES = 10000;

export function isMobileExportDevice() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(max-width: 768px)').matches ||
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
  );
}

export function isMobileSafari() {
  if (typeof navigator === 'undefined') return false;
  return (
    /Safari/i.test(navigator.userAgent) &&
    !/Chrome|CriOS|FxiOS|OPiOS|EdgiOS/i.test(navigator.userAgent) &&
    /Mobile|iP(ad|hone|od)/i.test(navigator.userAgent)
  );
}

export function getMemeCanvasDimensions(element) {
  const rect = element.getBoundingClientRect();
  const width = Math.ceil(rect.width) || element.clientWidth;
  const height = Math.ceil(rect.height) || element.clientHeight;
  return { width, height };
}

export function getSafeExportPixelRatio(element) {
  const { width, height } = getMemeCanvasDimensions(element);
  if (width <= 0 || height <= 0) {
    throw new Error(`Meme canvas has zero dimensions (${width}x${height})`);
  }

  const isMobile = isMobileExportDevice();
  const maxPixels = isMobile ? MAX_EXPORT_PIXELS_MOBILE : MAX_EXPORT_PIXELS_DESKTOP;
  const deviceRatio = window.devicePixelRatio || 1;
  let ratio = Math.min(deviceRatio, isMobile ? 1 : 2);

  while (width * height * ratio * ratio > maxPixels && ratio > 1) {
    ratio -= 0.25;
  }

  while (
    (width * ratio > MAX_EXPORT_DIMENSION || height * ratio > MAX_EXPORT_DIMENSION) &&
    ratio > 1
  ) {
    ratio -= 0.25;
  }

  return Math.max(1, ratio);
}

function getSvgImageHref(imageEl) {
  return (
    imageEl.getAttribute('href') ||
    imageEl.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
    ''
  );
}

function setSvgImageHref(imageEl, href) {
  imageEl.setAttribute('href', href);
  imageEl.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
}

/** Inline remote SVG pattern images as data URLs so mobile browsers can rasterize them. */
export async function inlineSvgPatternImages(root) {
  const images = root.querySelectorAll('image');
  const restores = [];

  await Promise.all(
    Array.from(images).map(async (imageEl) => {
      const href = getSvgImageHref(imageEl);
      if (!href || href.startsWith('data:')) return;

      try {
        const response = await fetch(href, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} for ${href}`);
        }
        const blob = await response.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });

        restores.push({ imageEl, href });
        setSvgImageHref(imageEl, dataUrl);
      } catch (err) {
        console.warn('[meme-export] Failed to inline SVG pattern image:', href, err);
      }
    })
  );

  return () => {
    for (const { imageEl, href } of restores) {
      setSvgImageHref(imageEl, href);
    }
  };
}

export function buildMemeExportOptions(element) {
  return {
    cacheBust: true,
    quality: 0.95,
    skipFonts: true,
    pixelRatio: getSafeExportPixelRatio(element),
    filter: (node) => !node.classList?.contains('export-exclude'),
    onImageErrorHandler: (event) => {
      const target = event?.target;
      const src =
        target?.src ||
        target?.href?.baseVal ||
        target?.getAttribute?.('href') ||
        'unknown';
      console.warn('[meme-export] Image failed during export embed:', src, event);
      return undefined;
    },
  };
}

/** Square export size for Miracle Calculator stat cards (download). */
export const STAT_CARD_EXPORT_PX = 1080;

/** Landscape OG / Facebook share size (1.91:1). */
export const STAT_CARD_SHARE_WIDTH = 1200;
export const STAT_CARD_SHARE_HEIGHT = 630;

/**
 * Capture at fixed pixel dimensions — decoupled from viewport-responsive layout.
 * Used when vw/clamp sizing would overflow the capture box on wide desktops.
 */
export async function captureFixedSizeBlob(
  element,
  width = STAT_CARD_EXPORT_PX,
  height = width
) {
  const restoreImages = await inlineSvgPatternImages(element);

  try {
    await waitForLayout();
    await waitForImages(element);

    const baseOptions = buildMemeExportOptions(element);
    const options = {
      ...baseOptions,
      width,
      height,
      pixelRatio: 1,
    };

    const maxAttempts = isMobileSafari() ? 4 : isMobileExportDevice() ? 3 : 2;
    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const blob = await toBlob(element, options);
        if (blob && blob.size >= MIN_BLOB_BYTES) {
          console.info('[meme-export] Fixed-size capture succeeded', {
            attempt: attempt + 1,
            blobBytes: blob.size,
            canvas: `${width}x${height}`,
          });
          return blob;
        }

        lastError = new Error(
          `Fixed export produced undersized blob (${blob?.size ?? 0} bytes) on attempt ${attempt + 1}`
        );
      } catch (err) {
        lastError = err;
        console.warn('[meme-export] Fixed-size toBlob attempt failed:', err, {
          attempt: attempt + 1,
          canvas: `${width}x${height}`,
        });
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
      }
    }

    throw lastError ?? new Error('Failed to generate fixed-size image');
  } finally {
    restoreImages();
  }
}

async function waitForLayout() {
  await new Promise((resolve) => requestAnimationFrame(resolve));
  await new Promise((resolve) => requestAnimationFrame(resolve));
}

/** Ensure <img> assets (e.g. flags) are decoded before rasterizing off-screen nodes. */
async function waitForImages(root) {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      if (typeof img.decode === 'function') {
        return img.decode().catch(() => undefined);
      }
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    })
  );
}

export async function captureMemeBlob(element) {
  await waitForLayout();

  const { width, height } = getMemeCanvasDimensions(element);
  const options = buildMemeExportOptions(element);
  const restoreImages = await inlineSvgPatternImages(element);

  try {
    const maxAttempts = isMobileSafari() ? 4 : isMobileExportDevice() ? 3 : 2;
    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const blob = await toBlob(element, options);
        if (blob && blob.size >= MIN_BLOB_BYTES) {
          console.info('[meme-export] Capture succeeded', {
            attempt: attempt + 1,
            blobBytes: blob.size,
            canvas: `${width}x${height}`,
            pixelRatio: options.pixelRatio,
          });
          return blob;
        }

        lastError = new Error(
          `Export produced undersized blob (${blob?.size ?? 0} bytes) on attempt ${attempt + 1}`
        );
        console.warn('[meme-export]', lastError.message, {
          canvas: `${width}x${height}`,
          pixelRatio: options.pixelRatio,
        });
      } catch (err) {
        lastError = err;
        console.warn('[meme-export] toBlob attempt failed:', err, {
          attempt: attempt + 1,
          canvas: `${width}x${height}`,
          pixelRatio: options.pixelRatio,
        });
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
      }
    }

    throw lastError ?? new Error('Failed to generate meme image');
  } finally {
    restoreImages();
  }
}
